/**
 * The generated command, its character budget, and the two things a diver
 * actually does with it: copy it, or save the code as a file.
 */

import { useState } from "react";

import type { CompileResult } from "../lib/compiler";
import { downloadSvg } from "../lib/qr";
import { MAX_SINGLE_QR_CHARS } from "../lib/tokens";

interface PayloadPanelProps {
  result: CompileResult;
  onCapture: () => void;
  canCapture: boolean;
}

export function PayloadPanel({ result, onCapture, canCapture }: PayloadPanelProps) {
  const [copied, setCopied] = useState(false);
  const used = result.charCount / MAX_SINGLE_QR_CHARS;

  return (
    <div>
      <span className="mb-1.5 block font-display text-[13px] font-semibold uppercase tracking-[0.16em] text-graphite-2">
        Command
      </span>

      <p className="m-0 mb-2.5 break-all border border-slate-line bg-field px-3 py-2.5 font-mono text-sm font-semibold leading-relaxed">
        {result.emitted.map((token, i) => (
          <span
            key={`${token.text}-${i}`}
            className={token.kind.startsWith("ext") ? "bg-smb/20" : undefined}
          >
            {token.text}
          </span>
        ))}
      </p>

      <div className="flex items-center gap-2.5 text-xs text-graphite-2">
        <span>
          {result.charCount} / {MAX_SINGLE_QR_CHARS} characters
        </span>
        <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-line">
          <i
            className="block h-full transition-[width] duration-200"
            style={{
              width: `${Math.min(used, 1) * 100}%`,
              background: used > 0.85 ? "var(--color-flag)" : "var(--color-deep)",
            }}
          />
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-[7px]">
        <button
          type="button"
          className="cursor-pointer rounded-[2px] border-none bg-graphite px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-paper"
          onClick={() => {
            void navigator.clipboard.writeText(result.payload).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1400);
            });
          }}
        >
          {copied ? "Copied" : "Copy command"}
        </button>

        <button
          type="button"
          className="cursor-pointer rounded-[2px] border-[1.5px] border-graphite bg-transparent px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-graphite"
          onClick={() => downloadSvg(result.payload, "dive-qr.svg")}
        >
          Save SVG
        </button>

        <button
          type="button"
          disabled={!canCapture}
          className="cursor-pointer rounded-[2px] border-[1.5px] border-deep bg-deep px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-paper disabled:cursor-not-allowed disabled:opacity-40"
          onClick={onCapture}
          title={canCapture ? undefined : "The slate is full. Remove a preset first."}
        >
          Add to slate
        </button>
      </div>
    </div>
  );
}

